import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Tabs,
    Tab,
    Badge,
    Spinner,
    Alert,
    Form,
    Table,
} from 'react-bootstrap';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import analyticsService from '@/shared/services/analyticsService';
import termService from '@/shared/services/termService';
import { useTerm } from '@/shared/contexts/TermContext';
import { PROGRAM_DISPLAY_ORDER } from '@/shared/utils/programOrder';
import './AnalyticsPage.css';
import { badgeTextFor } from '@/shared/utils/badgeText';

const PROGRAM_COLORS = {
    si: '#2E7D32',
    ta: '#1565C0',
    la: 'var(--psh-la-color)',
};

const PROGRAM_LABELS = {
    si: 'SI',
    ta: 'TA',
    la: 'LA',
};

/**
 * Coverage pie slices in PROGRAM_DISPLAY_ORDER, programs with no coverage
 * left out. Each slice carries its own color: the zero-value filter removes
 * slices, so a parallel color array indexed by position would shift.
 *
 * @param {{ si?: number, ta?: number, la?: number } | undefined} byProgram
 * @returns {{ name: string, value: number, color: string }[]}
 */
export function buildCoveragePieData(byProgram) {
    return PROGRAM_DISPLAY_ORDER.map((program) => ({
        name: PROGRAM_LABELS[program],
        value: byProgram?.[program] || 0,
        color: PROGRAM_COLORS[program],
    })).filter((d) => d.value > 0);
}

function LoadingSpinner({ message }) {
    return (
        <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">{message || 'Loading data...'}</p>
        </div>
    );
}

function ErrorAlert({ message, onDismiss }) {
    return (
        <Alert variant="danger" dismissible onClose={onDismiss}>
            {message}
        </Alert>
    );
}

function EmptyState({ message }) {
    return (
        <Card className="text-center py-5 border-0 bg-light">
            <Card.Body>
                <p className="text-muted mb-0">
                    {message || 'No data available for the selected term.'}
                </p>
            </Card.Body>
        </Card>
    );
}

/* ==============================
   Tab 1: Coverage
   ============================== */
function CoverageTab({ selectedTerm }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedTerm) return;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await analyticsService.getCoverageAnalytics({
                    term: selectedTerm,
                });
                setData(result);
            } catch (_err) {
                setError('Failed to load coverage analytics.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedTerm]);

    if (loading) return <LoadingSpinner message="Loading coverage data..." />;
    if (error)
        return <ErrorAlert message={error} onDismiss={() => setError(null)} />;
    if (!data) return <EmptyState />;

    const pieData = buildCoveragePieData(data.byProgram);

    return (
        <>
            {/* Coverage Rate Summary */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Overall Coverage Rate
                            </h2>
                            <Badge
                                bg={
                                    data.coverageRate >= 50
                                        ? 'success'
                                        : 'warning'
                                }
                                text={badgeTextFor(
                                    data.coverageRate >= 50
                                        ? 'success'
                                        : 'warning'
                                )}
                                className="fs-2 px-3 py-2"
                            >
                                {data.coverageRate}%
                            </Badge>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Courses with Support
                            </h2>
                            <p className="fs-3 fw-bold text-success mb-0">
                                {data.supportedCourses}
                            </p>
                            <small className="text-muted">
                                of {data.totalCourses} total
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Coverage Gaps
                            </h2>
                            <p className="fs-3 fw-bold text-danger mb-0">
                                {data.unsupportedCourses}
                            </p>
                            <small className="text-muted">
                                courses without support
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                {/* Program Split Pie Chart */}
                <Col md={5}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Support by Program</h2>
                            {pieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            dataKey="value"
                                            label={({ name, value }) =>
                                                `${name}: ${value}`
                                            }
                                        >
                                            {pieData.map((entry) => (
                                                <Cell
                                                    key={`cell-${entry.name}`}
                                                    fill={entry.color}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted text-center py-4">
                                    No program data available.
                                </p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Department Coverage Table */}
                <Col md={7}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Department Coverage</h2>
                            {data.departmentCoverage?.length > 0 ? (
                                <Table striped hover size="sm" responsive>
                                    <thead>
                                        <tr>
                                            <th>Department</th>
                                            <th className="text-center">
                                                Supported
                                            </th>
                                            <th className="text-center">
                                                Assignments
                                            </th>
                                            <th className="text-center">
                                                Programs
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.departmentCoverage.map((dept) => (
                                            <tr key={dept.department}>
                                                <td>{dept.department}</td>
                                                <td className="text-center">
                                                    {dept.supportedCourseCount}
                                                </td>
                                                <td className="text-center">
                                                    {dept.totalAssignments}
                                                </td>
                                                <td className="text-center">
                                                    {dept.programsActive?.map(
                                                        (p) => (
                                                            <Badge
                                                                key={p}
                                                                className="analytics-program-badge me-1"
                                                                data-program={p}
                                                            >
                                                                {PROGRAM_LABELS[
                                                                    p
                                                                ] || p}
                                                            </Badge>
                                                        )
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted text-center py-4">
                                    No department coverage data.
                                </p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

/* ==============================
   Tab 2: Volume & Conversion
   ============================== */
function ConversionTab({ selectedTerm }) {
    const [funnelData, setFunnelData] = useState(null);
    const [trendsData, setTrendsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedTerm) return;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const [conversion, trends] = await Promise.all([
                    analyticsService.getConversion(selectedTerm),
                    analyticsService.getTrends(),
                ]);
                setFunnelData(conversion);
                setTrendsData(trends);
            } catch (_err) {
                setError('Failed to load conversion analytics.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedTerm]);

    if (loading) return <LoadingSpinner message="Loading conversion data..." />;
    if (error)
        return <ErrorAlert message={error} onDismiss={() => setError(null)} />;
    if (!funnelData) return <EmptyState />;

    const funnelChartData = funnelData.funnel || [];

    return (
        <>
            {/* Conversion Funnel */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h2 className="h6 mb-3">
                                Application Conversion Funnel
                            </h2>
                            {funnelChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={funnelChartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="stage" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar
                                            dataKey="count"
                                            fill="#1565C0"
                                            name="Total"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No funnel data available." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Funnel Breakdown by Program */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <h2 className="h6 mb-3">Funnel by Program</h2>
                            {funnelChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={funnelChartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="stage" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        {PROGRAM_DISPLAY_ORDER.map(
                                            (program) => (
                                                <Bar
                                                    key={program}
                                                    dataKey={`byProgram.${program}`}
                                                    fill={
                                                        PROGRAM_COLORS[program]
                                                    }
                                                    name={
                                                        PROGRAM_LABELS[program]
                                                    }
                                                />
                                            )
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No program breakdown available." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Volume Trend */}
            {trendsData?.applications?.length > 0 && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <h2 className="h6 mb-3">
                                    Application Volume by Term
                                </h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={trendsData.applications}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="term" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#1565C0"
                                            strokeWidth={2}
                                            name="Applications"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </>
    );
}

/* ==============================
   Tab 3: Distribution
   ============================== */
function DistributionTab({ selectedTerm }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedTerm) return;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result =
                    await analyticsService.getDistribution(selectedTerm);
                setData(result);
            } catch (_err) {
                setError('Failed to load distribution analytics.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedTerm]);

    if (loading)
        return <LoadingSpinner message="Loading distribution data..." />;
    if (error)
        return <ErrorAlert message={error} onDismiss={() => setError(null)} />;
    if (!data) return <EmptyState />;

    const programData = (data.byProgram || []).map((item) => ({
        ...item,
        fill: PROGRAM_COLORS[item.name] || '#999999',
    }));

    return (
        <>
            {/* By Program */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Awards by Program</h2>
                            {programData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={programData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tickFormatter={(v) =>
                                                PROGRAM_LABELS[v] || v
                                            }
                                        />
                                        <YAxis />
                                        <Tooltip
                                            formatter={(
                                                value,
                                                _name,
                                                props
                                            ) => [
                                                value,
                                                PROGRAM_LABELS[
                                                    props.payload.name
                                                ] || props.payload.name,
                                            ]}
                                        />
                                        <Bar dataKey="count" name="Awards">
                                            {programData.map((entry) => (
                                                <Cell
                                                    key={`cell-${entry.name}`}
                                                    fill={entry.fill}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No program distribution data." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* By Department */}
                <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Awards by Department</h2>
                            {data.byDepartment?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={data.byDepartment}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar
                                            dataKey="count"
                                            fill="#1565C0"
                                            name="Awards"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No department data." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* By Course Level and Modality */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Awards by Course Level</h2>
                            {data.byCourseLevel?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={data.byCourseLevel.sort((a, b) =>
                                            a.name.localeCompare(b.name)
                                        )}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tickFormatter={(v) =>
                                                v === 'Other' ? v : `${v}-level`
                                            }
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar
                                            dataKey="count"
                                            fill="#2E7D32"
                                            name="Awards"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No course level data." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h2 className="h6 mb-3">Awards by Modality</h2>
                            {data.byModality?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={data.byModality}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            tickFormatter={(v) =>
                                                v
                                                    .replace(/_/g, ' ')
                                                    .replace(/\b\w/g, (c) =>
                                                        c.toUpperCase()
                                                    )
                                            }
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar
                                            dataKey="count"
                                            fill="var(--psh-la-color)"
                                            name="Awards"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState message="No modality data." />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

/* ==============================
   Tab 4: Outcomes
   ============================== */
function OutcomesTab({ selectedTerm }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedTerm) return;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await analyticsService.getOutcomes(selectedTerm);
                setData(result);
            } catch (_err) {
                setError('Failed to load outcomes analytics.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedTerm]);

    if (loading) return <LoadingSpinner message="Loading outcomes data..." />;
    if (error)
        return <ErrorAlert message={error} onDismiss={() => setError(null)} />;
    if (!data) return <EmptyState />;

    const hasDfwData =
        data.avgDfwSupported != null || data.avgDfwUnsupported != null;

    const comparisonData = [
        {
            name: 'Supported',
            avgDfwRate: data.avgDfwSupported || 0,
            count: data.supportedCount,
        },
        {
            name: 'Unsupported',
            avgDfwRate: data.avgDfwUnsupported || 0,
            count: data.unsupportedCount,
        },
    ];

    return (
        <>
            {!hasDfwData && (
                <Alert variant="info" className="mb-4">
                    DFW rate data has not been populated for courses in this
                    term. Import historical DFW data from Banner/Workday to see
                    outcome comparisons between supported and unsupported
                    courses.
                </Alert>
            )}

            {/* Summary Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Supported Courses
                            </h2>
                            <p className="fs-3 fw-bold text-success mb-0">
                                {data.supportedCount}
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Unsupported Courses
                            </h2>
                            <p className="fs-3 fw-bold text-secondary mb-0">
                                {data.unsupportedCount}
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Avg DFW (Supported)
                            </h2>
                            <p className="fs-3 fw-bold text-success mb-0">
                                {data.avgDfwSupported != null
                                    ? `${data.avgDfwSupported}%`
                                    : 'N/A'}
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm text-center">
                        <Card.Body>
                            <h2 className="h6 text-muted mb-2">
                                Avg DFW (Unsupported)
                            </h2>
                            <p className="fs-3 fw-bold text-danger mb-0">
                                {data.avgDfwUnsupported != null
                                    ? `${data.avgDfwUnsupported}%`
                                    : 'N/A'}
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* DFW Comparison Chart */}
            {hasDfwData && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <h2 className="h6 mb-3">
                                    Average DFW Rate: Supported vs Unsupported
                                </h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={comparisonData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis
                                            label={{
                                                value: 'DFW Rate (%)',
                                                angle: -90,
                                                position: 'insideLeft',
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) => [
                                                `${value}%`,
                                                'Avg DFW Rate',
                                            ]}
                                        />
                                        <Bar
                                            dataKey="avgDfwRate"
                                            name="Avg DFW Rate"
                                        >
                                            <Cell fill="#2E7D32" />
                                            <Cell fill="#d32f2f" />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Department DFW Table */}
            {data.byDepartment?.length > 0 && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <h2 className="h6 mb-3">
                                    DFW Comparison by Department
                                </h2>
                                <Table striped hover size="sm" responsive>
                                    <thead>
                                        <tr>
                                            <th>Department</th>
                                            <th className="text-center">
                                                Supported
                                            </th>
                                            <th className="text-center">
                                                Unsupported
                                            </th>
                                            <th className="text-center">
                                                Avg DFW (Supported)
                                            </th>
                                            <th className="text-center">
                                                Avg DFW (Unsupported)
                                            </th>
                                            <th className="text-center">
                                                DFW Delta
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byDepartment.map((dept) => (
                                            <tr key={dept.department}>
                                                <td>{dept.department}</td>
                                                <td className="text-center">
                                                    {dept.supportedCount}
                                                </td>
                                                <td className="text-center">
                                                    {dept.unsupportedCount}
                                                </td>
                                                <td className="text-center">
                                                    {dept.avgDfwSupported !=
                                                    null
                                                        ? `${dept.avgDfwSupported}%`
                                                        : 'N/A'}
                                                </td>
                                                <td className="text-center">
                                                    {dept.avgDfwUnsupported !=
                                                    null
                                                        ? `${dept.avgDfwUnsupported}%`
                                                        : 'N/A'}
                                                </td>
                                                <td className="text-center">
                                                    {dept.dfwDelta != null ? (
                                                        <Badge
                                                            bg={
                                                                dept.dfwDelta >
                                                                0
                                                                    ? 'success'
                                                                    : dept.dfwDelta <
                                                                        0
                                                                      ? 'danger'
                                                                      : 'secondary'
                                                            }
                                                            text={badgeTextFor(
                                                                dept.dfwDelta >
                                                                    0
                                                                    ? 'success'
                                                                    : dept.dfwDelta <
                                                                        0
                                                                      ? 'danger'
                                                                      : 'secondary'
                                                            )}
                                                        >
                                                            {dept.dfwDelta > 0
                                                                ? '+'
                                                                : ''}
                                                            {dept.dfwDelta}%
                                                        </Badge>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </>
    );
}

/* ==============================
   Tab 5: Trends
   ============================== */
function TrendsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await analyticsService.getTrends();
                setData(result);
            } catch (_err) {
                setError('Failed to load trends analytics.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) return <LoadingSpinner message="Loading trends data..." />;
    if (error)
        return <ErrorAlert message={error} onDismiss={() => setError(null)} />;
    if (!data) return <EmptyState />;

    const hasApplications = data.applications?.length > 0;
    const hasAssignments = data.assignments?.length > 0;

    return (
        <>
            {/* Applications per Term */}
            {hasApplications && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <h2 className="h6 mb-3">
                                    Applications per Term
                                </h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={data.applications}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="term" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#1565C0"
                                            strokeWidth={2}
                                            name="Applications"
                                            dot={{ r: 4 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Assignments per Term by Program */}
            {hasAssignments && (
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <h2 className="h6 mb-3">
                                    Assignments per Term by Program
                                </h2>
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={data.assignments}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="term" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="si"
                                            stroke={PROGRAM_COLORS.si}
                                            strokeWidth={2}
                                            name="SI"
                                            dot={{ r: 4 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="ta"
                                            stroke={PROGRAM_COLORS.ta}
                                            strokeWidth={2}
                                            name="TA"
                                            dot={{ r: 4 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="la"
                                            stroke={PROGRAM_COLORS.la}
                                            strokeWidth={2}
                                            name="LA"
                                            dot={{ r: 4 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {!hasApplications && !hasAssignments && (
                <EmptyState message="No trend data available yet. Data will appear as terms accumulate." />
            )}
        </>
    );
}

/* ==============================
   Main AnalyticsPage
   ============================== */
function AnalyticsPage() {
    const { currentTermCode } = useTerm();
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState('');
    const [termsLoading, setTermsLoading] = useState(true);

    // Load available terms
    const loadTerms = useCallback(async () => {
        try {
            setTermsLoading(true);
            const allTerms = await termService.getAllTerms();
            setTerms(allTerms);
            // Default to current term if available
            if (currentTermCode) {
                setSelectedTerm(currentTermCode);
            } else if (allTerms.length > 0) {
                setSelectedTerm(allTerms[0].code);
            }
        } catch (_err) {
            // term-load failure leaves selectedTerm empty; child tabs show
            // their own empty-state — no page-level error needed here
        } finally {
            setTermsLoading(false);
        }
    }, [currentTermCode]);

    useEffect(() => {
        loadTerms();
    }, [loadTerms]);

    // Update selected term when currentTermCode becomes available
    useEffect(() => {
        if (currentTermCode && !selectedTerm) {
            setSelectedTerm(currentTermCode);
        }
    }, [currentTermCode, selectedTerm]);

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>Analytics Dashboard</h1>
                    <p className="text-muted">
                        Cross-program analytics for peer support coverage,
                        outcomes, and trends.
                    </p>
                </div>
                <div className="analytics-term-select-wrapper">
                    {termsLoading ? (
                        <Spinner
                            animation="border"
                            size="sm"
                            variant="primary"
                            role="status"
                        >
                            <span className="visually-hidden">
                                Loading terms...
                            </span>
                        </Spinner>
                    ) : (
                        <Form.Select
                            value={selectedTerm}
                            onChange={(e) => setSelectedTerm(e.target.value)}
                            aria-label="Select academic term"
                        >
                            {terms.length === 0 && (
                                <option value="">No terms available</option>
                            )}
                            {terms.map((t) => (
                                <option key={t.code} value={t.code}>
                                    {t.name || t.code}
                                </option>
                            ))}
                        </Form.Select>
                    )}
                </div>
            </div>

            {!selectedTerm && !termsLoading && (
                <EmptyState message="No academic term selected. Configure terms in the admin settings." />
            )}

            {selectedTerm && (
                <Tabs
                    defaultActiveKey="coverage"
                    className="mb-4"
                    id="analytics-tabs"
                >
                    <Tab eventKey="coverage" title="Coverage">
                        <CoverageTab selectedTerm={selectedTerm} />
                    </Tab>
                    <Tab eventKey="conversion" title="Volume & Conversion">
                        <ConversionTab selectedTerm={selectedTerm} />
                    </Tab>
                    <Tab eventKey="distribution" title="Distribution">
                        <DistributionTab selectedTerm={selectedTerm} />
                    </Tab>
                    <Tab eventKey="outcomes" title="Outcomes">
                        <OutcomesTab selectedTerm={selectedTerm} />
                    </Tab>
                    <Tab eventKey="trends" title="Trends">
                        <TrendsTab />
                    </Tab>
                </Tabs>
            )}
        </Container>
    );
}

export default AnalyticsPage;
